import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { usePrefs } from "@/lib/context";
import { useCategories, catName } from "@/lib/hooks";
import { MetersEditor } from "@/components/Meters";
import { Field } from "@/components/PropertyDialog";

const EMPTY = { reference: "", type: "appartement", building: "", floor: "", surface: "", rooms: "", is_common: false, equipment: "", meters: [] };

export function UnitDialog({ propertyId, ownerIds, trigger }) {
  const { t, lang } = usePrefs();
  const cats = useCategories();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    try {
      await api.post(`/properties/${propertyId}/units`, {
        ...f, surface: f.surface ? Number(f.surface) : null, rooms: f.rooms ? Number(f.rooms) : null, owner_ids: ownerIds,
        equipment: f.equipment.split(",").map((s) => s.trim()).filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: ["property", propertyId] });
      setOpen(false);
      setF(EMPTY);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg rounded-sm" data-testid="unit-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{t("newUnit")}</DialogTitle></DialogHeader>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.is_common} onCheckedChange={(v) => setF({ ...f, is_common: !!v, type: v ? "Hall d'entrée" : "appartement" })} data-testid="unit-common-checkbox" />{t("commun")}</label>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("reference")}><Input value={f.reference} onChange={set("reference")} data-testid="unit-reference-input" /></Field>
          <Field label={t("type")}>
            {f.is_common ? <Input value={f.type} onChange={set("type")} placeholder="Hall, jardin…" data-testid="unit-type-input" /> : (
              <Select value={f.type} onValueChange={set("type")}>
                <SelectTrigger data-testid="unit-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.property.filter((c) => !["immeuble", "residence"].includes(c.key)).map((c) => <SelectItem key={c.key} value={c.key}>{catName(c, lang)}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </Field>
          <Field label={t("building")}><Input value={f.building} onChange={set("building")} data-testid="unit-building-input" /></Field>
          <Field label={t("floor")}><Input value={f.floor} onChange={set("floor")} data-testid="unit-floor-input" /></Field>
          <Field label={t("surface")}><Input type="number" value={f.surface} onChange={set("surface")} data-testid="unit-surface-input" /></Field>
          <Field label={t("rooms")}><Input type="number" value={f.rooms} onChange={set("rooms")} data-testid="unit-rooms-input" /></Field>
          <div className="col-span-2"><Field label={t("equipment")}><Input value={f.equipment} onChange={set("equipment")} data-testid="unit-equipment-input" /></Field></div>
          <div className="col-span-2"><Field label={t("meters")}><MetersEditor value={f.meters} onChange={set("meters")} /></Field></div>
        </div>
        <Button onClick={save} disabled={!f.reference} className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="unit-save-button">{t("create")}</Button>
      </DialogContent>
    </Dialog>
  );
}
